import { useEffect, useState } from "react";
import { Download, History, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const when = (s) => (s ? new Date(s).toLocaleString(undefined,
  { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");
const shorten = (v) => {
  if (Array.isArray(v)) return v.join(", ") || "nobody";
  if (v === true) return "on";
  if (v === false) return "off";
  return String(v ?? "—");
};

/** Who changed which app's access, and when. Ninety days, downloadable. */
export default function AppAudienceChangesCard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/app-audiences/changes?days=90")
      .then((r) => setRows(r.data.changes || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const csv = () => {
    const head = ["When", "Who", "App", "What changed", "From", "To", "Detail"];
    const lines = [head.join(",")];
    rows.forEach((r) => {
      const cs = r.changes?.length ? r.changes : [{ field: "", from: "", to: "" }];
      cs.forEach((c) => lines.push([r.at, r.who, r.app, c.field,
        shorten(c.from), shorten(c.to), r.detail]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")));
    });
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `app-access-changes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="px-5 py-4" data-testid="app-audience-changes-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-slate-800 dark:text-slate-100">
            <History className="h-4 w-4 text-slate-400" /> App access changes
          </h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Every change to who can see an app, last 90 days.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={csv} disabled={!rows.length}
          data-testid="app-audience-changes-csv">
          <Download className="mr-1.5 h-3.5 w-3.5" /> Download CSV
        </Button>
      </div>

      {loading ? (
        <div className="flex h-20 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-slate-300 dark:text-slate-600" />
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="app-audience-changes-empty">
          No access changes in the last 90 days.
        </p>
      ) : (
        <div className="mt-3 max-h-[320px] overflow-y-auto">
          <table className="w-full text-xs">
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.at}-${i}`} data-testid={`app-audience-change-${i}`}
                  className="border-b border-slate-50 dark:border-slate-800/60">
                  <td className="whitespace-nowrap py-1.5 pr-3 text-slate-400 dark:text-slate-500">{when(r.at)}</td>
                  <td className="py-1.5 pr-3 font-medium text-slate-700 dark:text-slate-200">{r.who}</td>
                  <td className="py-1.5 pr-3 text-slate-600 dark:text-slate-300">{r.app || "—"}</td>
                  <td className="py-1.5 text-slate-500 dark:text-slate-400">
                    {r.changes?.length
                      ? r.changes.map((c) => (
                        <span key={c.field} className="mr-2">
                          <span className="font-medium">{c.field}</span>: {shorten(c.from)} → {shorten(c.to)}
                        </span>
                      ))
                      : r.detail}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
